import { gql } from "@apollo/client";
import { API } from "api/API";
import axios from "axios";
import { USERTYPE } from "constants/constants";

const APIInstance = new API();

export const getCorporations = gql`
    query GetCorporations($user_type_id: Int!) {
        corporate(order_by: { created_at: desc }) {
            corporate_id
            name
            address_1
            address_2
            city_id
            country_id
            state_id
            zip_code
            phone_number
            city_name
            state_name
            created_at
            corporate_dashboard_enabled
            divisions {
                division_id
                name
                regions {
                    region_id
                    name
                    mills {
                        mill_id
                    }
                }
            }
            media {
                media_id
                media_url
            }
            corporate_module_licenses {
                expires_at
                module_license {
                    module_license_id
                    name
                }
            }
            users(where: { user_type_id: { _eq: $user_type_id }, is_enabled: { _eq: true } }) {
                first_name
                last_name
                email
                user_id
                is_enabled
            }
            mills(limit: 2) {
                mill_id
            }
        }
    }
`;

export const getCorporationCountryCode = gql`
    query GetCorporationCountryCode($corporate_id: Int!) {
        corporate(where: { corporate_id: { _eq: $corporate_id } }) {
            country {
                code
            }
        }
    }
`;

export const getCountries = gql`
    query GetCountries {
        country(order_by: { name: asc }) {
            id
            name
            code
            numeric_code
        }
    }
`;

export const getStatesByCountryId = gql`
    query GetStatesByCountryId($country_id: Int!) {
        state(where: { country_id: { _eq: $country_id } }, order_by: { name: asc }) {
            id
            name
            country_id
        }
    }
`;

export const getCitiesByStateId = gql`
    query GetCitiesByStateId($state_id: Int!) {
        city(where: { state_id: { _eq: $state_id } }, order_by: { name: asc }) {
            id
            name
            state_id
        }
    }
`;

export const insertCorporationWithAdmin = gql`
    mutation registerCorporate($corporate: corporate_insert_input!) {
        insert_corporate_one(object: $corporate) {
            corporate_id
            name
            address_1
            address_2
            city_id
            country_id
            state_id
            state_name
            city_name
            zip_code
            phone_number
            divisions {
                division_id
                name
                regions {
                    region_id
                    name
                }
            }
            users {
                user_id
                email
                first_name
                last_name
                user_type {
                    name
                    default_user_type_translations {
                        user_type_id
                        language {
                            language_id
                            name
                            code
                        }
                        translated_name
                    }
                }
            }
        }
    }
`;

export const upsertCorporationWithAdminUser = gql`
    mutation upsertCorporationWithAdminUser(
        $corporate: [corporate_insert_input!]!
        $division: [division_insert_input!]!
    ) {
        insert_corporate(
            objects: $corporate
            on_conflict: {
                constraint: corporate_pkey
                update_columns: [
                    name
                    phone_number
                    zip_code
                    address_2
                    address_1
                    country_id
                    state_id
                    city_id
                    state_name
                    city_name
                ]
            }
        ) {
            affected_rows
            returning {
                corporate_id
                name
                address_1
                address_2
                city_id
                country_id
                state_id
                zip_code
                phone_number
                divisions {
                    division_id
                    name
                    regions {
                        region_id
                        name
                    }
                }
                users {
                    user_id
                    email
                    first_name
                    last_name
                    user_type {
                        name
                        default_user_type_translations {
                            user_type_id
                            language {
                                language_id
                                name
                                code
                            }
                            translated_name
                        }
                    }
                }
            }
        }
        insert_division(
            objects: $division
            on_conflict: { constraint: division_pkey, update_columns: [name, deleted_at] }
        ) {
            affected_rows
            returning {
                division_id
                regions {
                    region_id
                }
            }
        }
    }
`;

export const getCorporationAndMillBackOffice = gql`
    query GetCorporationsAndMillBackOffice($where: mill_bool_exp) {
        corporate {
            corporate_id
            name
            users (where: {_and: [{user_type_id: { _eq: ${USERTYPE.ADMINISTRATOR} }}, {is_enabled: {_eq: true}}]}){
                email
                first_name
                last_name
                user_type {
                    user_type_id
                    name
                    default_user_type_translations{ 
                      user_type_id
                        language {
                          language_id
                          name
                          code
                        }
                        translated_name
                  }
                }
            }
            address_1
            phone_number
            corporate_data {
                corporate_data_id
                url
                holding_company {
                    holding_company_id
                    name
                    url
                    address
                }
            }
            divisions {
                division_id
                name
                regions {
                    region_id
                    name
                    mills {
                        mill_id
                    }
                }
            }
        }
        mill(where: $where) {
            mill_id
            corporate_id
            mill_data {
                mill_data_id
                language_id
                first_day_reporting_week
                day_reporting_week {
                  day_id
                  name
                default_day_translations {
                    default_day_translation_id
                    language {
                        language_id
                        name
                    }
                    translated_name
                }
              }
                measure_metric_id
                timezone
                first_day_fiscal_year
            }
            crews {
                crew_id
                name
            }
            mill_key_success_factors {
                mill_key_success_factors_id
                is_enabled
                key_success_factor {
                    key_success_factors_id
                    name
                    default_key_success_factor_translations {
                      key_success_factor_id
                      language {
                        language_id
                        name
                        code
                      }
                      translated_name
                    }
                }
            }
            production_lines{
              production_line_id
              production_line_shifts(where: {disabled_at: {_is_null: true}}){
                production_line {
                  production_line_id
                  name
                }
                shift{
                shift_id
                name
                  shift_historic(
                        order_by: { effective_at: desc }
                        limit: 1) {
                    shift_historic_id
                    effective_at
                    end_time
                    start_time
                    shift_days {
                      shift_day_id
                      day_id
                      is_enabled
                      day {
                      day_id
                        name
                        default_day_translations {
                            default_day_translation_id
                            language {
                                language_id
                                name
                            }
                            translated_name
                        }
                      }
                    }
                  }
                }
              }
            }
            shifts(order_by: {created_at: desc}, where: {disabled_at: {_is_null: true}}) {
                shift_id
                name
                  shift_historic(order_by: {effective_at: desc}, limit: 1) {
                    shift_historic_id
                    end_time
                    start_time
                    effective_at
                    shift_days {
                      shift_day_id
                      day_id
                      is_enabled
                    }
                  }
                  production_line_shifts(where: {disabled_at: {_is_null: true}, production_line: {deleted_at: {_is_null: true}}}){
                    production_line_shift_id
                    production_line {
                      production_line_id
                      name
                    }
                  }
              }
        }
    }
`;

export const upsertCorporationData = gql`
    mutation updateCorporateData(
        $corporate_id: Int!
        $corporateChanges: corporate_set_input!
        $corporate_data: corporate_data_insert_input!
        $division: [division_insert_input!]!
    ) {
        update_corporate(where: { corporate_id: { _eq: $corporate_id } }, _set: $corporateChanges) {
            affected_rows
            returning {
                corporate_id
            }
        }
        insert_corporate_data_one(
            object: $corporate_data
            on_conflict: { constraint: corporate_data_pkey, update_columns: [url] }
        ) {
            corporate_data_id
            holding_company {
                name
            }
        }
        insert_division(
            objects: $division
            on_conflict: { constraint: division_pkey, update_columns: [name, deleted_at] }
        ) {
            affected_rows
            returning {
                division_id
                regions {
                    region_id
                }
            }
        }
    }
`;

export const activateCorporateModuleLicense = gql`
    mutation activateCorporateModuleLicense($corporate_module_license: corporate_module_license_insert_input!) {
        insert_corporate_module_license_one(object: $corporate_module_license) {
            corporate_id
            module_license_id
        }
    }
`;

export const deactivateOrReactivateCorporateModuleLicense = gql`
    mutation deactivateCorporateModuleLicense($corporate_id: Int!, $module_license_id: Int!, $expires_at: timestamp!) {
        update_corporate_module_license(
            where: { _and: { corporate_id: { _eq: $corporate_id }, module_license_id: { _eq: $module_license_id } } }
            _set: { expires_at: $expires_at }
        ) {
            affected_rows
        }
    }
`;

export const getCorporationDetail = gql`
    query getCorporationDetail($corporate_id: Int!) {
        corporate(where: { corporate_id: { _eq: $corporate_id } }) {
            corporate_id
            name
            divisions {
                division_id
                name
                regions {
                    region_id
                    name
                    mills {
                        mill_id
                        name
                        cluster_enabled
                        is_enabled
                        created_at
                        physical_address
                        physical_address_2
                        physical_state_name
                        physical_city_name
                        physical_zip_code
                        physical_country_id
                        billing_address
                        billing_address_2
                        billing_state_name
                        billing_city_name
                        billing_zip_code
                        billing_country_id
                        users(where: { _or:[
                            {user_type_id: { _eq: ${USERTYPE.ADMINISTRATOR} }},
                            {user_type_id: { _eq: ${USERTYPE.MANAGER} }}
                        ], is_enabled: {_eq: true} }) {
                            first_name
                            last_name
                            user_id
                            email
                            user_type_id
                            is_enabled
                        }
                        naics_code
                        code
                        phone_number
                        url
                        division {
			                division_id
                        }
                        region {
                            region_id
                        }
                        country {
                            id
                        }
                    }
                }
            }
            corporate_module_licenses {
                module_license {
                    module_license_id
                    name
                }
            }
        }
        mill(where: { corporate_id: { _eq: $corporate_id }, division_id: {_is_null: true} }) {
                        mill_id
                        name
                        is_enabled
                        created_at
                        physical_address
                        physical_address_2
                        physical_state_name
                        physical_city_name
                        physical_zip_code
                        physical_country_id
                        billing_address
                        billing_address_2
                        billing_state_name
                        billing_city_name
                        billing_zip_code
                        billing_country_id
                        cluster_enabled
                        users(where: { _or:[
                            {user_type_id: { _eq: ${USERTYPE.ADMINISTRATOR} }},
                            {user_type_id: { _eq: ${USERTYPE.MANAGER} }}
                        ], is_enabled: {_eq: true} }) {
                            first_name
                            last_name
                            user_id
                            email
                            user_type_id
                            is_enabled
                        }
                        naics_code
                        code
                        phone_number
                        url
                        division {
			                division_id
                        }
                        region {
                            region_id
                        }
                        country {
                            id
                        }
                        
        }
    }
`;

export const getCorporateAllDivisionAllRegionsAllMills = gql`
    query getAllCorporations {
        corporate {
            corporate_id
            name
            mills(where: { division_id: { _is_null: true } }) {
                mill_id
                name
            }
            divisions {
                division_id
                name
                regions {
                    region_id
                    name
                    mills {
                        mill_id
                        name
                    }
                }
            }
        }
    }
`;

export const uploadCorporateImage = async (
    corporateId: number,
    corporateImageFormData: FormData,
): Promise<{ publicUrl: string } | unknown> => {
    try {
        const url = `${APIInstance.api_root}/upload/corporate-image/${corporateId}`;
        const response = await axios.put<{ publicUrl: string }>(url, corporateImageFormData);

        return response;
    } catch (error) {
        console.error(error);
        return error;
    }
};
export const GetCorporationAdmins = gql`
    query getCorporationAdmins($corporate_id: Int!, $user_type_id: Int!) {
        corporate(where: { corporate_id: { _eq: $corporate_id } }) {
            users(where: { user_type_id: { _eq: $user_type_id }, is_enabled: { _eq: true } }) {
                first_name
                last_name
                email
            }
        }
    }
`;

export const getAllKeySuccessFactors = gql`
    query getAllKeySuccessFactors {
        key_success_factors {
            key_success_factors_id
            name
        }
    }
`;

export const getAllFailureTypes = gql`
    query getAllKeySuccessFactors {
        failure_type {
            failure_type_id
            name
        }
    }
`;

export const updateCorporateDashboardFeature = gql`
    mutation updateCorporateDashboardFeature($corporate_id: Int!, $corporate_dashboard_enabled: Boolean!) {
        update_corporate(
            where: { corporate_id: { _eq: $corporate_id } }
            _set: { corporate_dashboard_enabled: $corporate_dashboard_enabled }
        ) {
            affected_rows
        }
    }
`;
